import { mapKanbanConfigs } from "@/backend/read/mapBackendData";
import { emitStickyChanged } from "@/composables/useEventBus";
import { captureException } from "@/error/sentry";
import { maxMoveSendPerPeriod, moveSendPerPeriod, moveSendPeriod } from "@/model/Settings";
import type { ArtId } from "@/model/baseTypes";
import type { Kanban } from "@/model/board";
import type { Card, CardMeta, Reaction } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import type { Objective } from "@/model/objective";
import type { Shape } from "@/model/shape";
import type { TimerData } from "@/model/timer";
import { useLicenseStore } from "@/store/license";
import { isZombieType } from "@/store/stickyType";
import { Throttle } from "@/utils/Throttle";
import { stickerToString } from "@/utils/sticker";

import { retrieveBackendSession } from "../impl/backendSessionCache";
import type { ServerLink, ServerObjective, ServerSticky } from "../serverModel";
import type { BackendWriteSession } from "./BackendWriteSession";
import { backendWriteSessionDummy } from "./backendWriteSessionDummy";

interface CardMove {
  boardId: string;
  coord: RelativeCoordinate;
}

export type AlterCard = Partial<Card & RelativeCoordinate & Pick<CardMeta, "zIndex">>;

export class BackendWriter {
  backendSess: BackendWriteSession = retrieveBackendSession() || backendWriteSessionDummy();
  cardMoves = new Map<string, CardMove>();

  constructor() {
    new Throttle(moveSendPeriod, moveSendPerPeriod, maxMoveSendPerPeriod, () => {
      const moved = this.cardMoves.size;
      for (const [id, move] of this.cardMoves) {
        this.alterCard(move.boardId, id, move.coord);
      }
      this.cardMoves.clear();
      return moved;
    });
  }

  initSession(session: BackendWriteSession) {
    this.backendSess = useLicenseStore().license.usable ? session : backendWriteSessionDummy();
  }

  syncIteration(teamId: string, iterationId: number) {
    return this.backendSess.syncIteration(teamId, iterationId);
  }

  syncBacklog(artId?: ArtId) {
    return this.backendSess.syncBacklog(artId);
  }

  startAlterCard(boardId: string, id: string) {
    this.backendSess.boardPublish(boardId, "sticky.start_update", [id]);
  }

  alterCard(boardId: string, id: string, props: AlterCard, priorities?: boolean) {
    this.backendSess.boardPublish(boardId, "sticky.update", [
      id,
      toServerSticky(props, priorities),
    ]);
    emitStickyChanged(id);
  }

  stopAlterCard(boardId: string, id: string, props?: AlterCard, priorities?: boolean) {
    if (!id) {
      captureException(Error("Stop alter sticky without id"));
      return;
    }
    this.backendSess.boardPublish(boardId, "sticky.stop_update", [
      id,
      toServerSticky(props, priorities),
    ]);
    emitStickyChanged(id);
  }

  moveCard(boardId: string, id: string, coord: RelativeCoordinate) {
    this.cardMoves.set(id, { boardId, coord });
  }

  addCard(boardId: string, props: AlterCard): Promise<string> {
    return this.backendSess.newSticky(boardId, toServerSticky(props));
  }

  deleteCard(boardId: string, id: string, deleteInAlm: boolean) {
    this.stopAlterCard(boardId, id, {});
    void this.backendSess.deleteCard(id, { fromAlm: false, deleteInAlm: deleteInAlm });
  }

  scaleBoard(boardId: string, value: number) {
    this.backendSess.boardPublish(boardId, "sticky.scale", [value]);
  }

  frontCard(boardId: string, ids: string[]) {
    this.backendSess.boardPublish(boardId, "sticky.bring_to_front", [ids]);
  }

  setVelocity(boardId: string, iteration: number, velocity: number) {
    void this.backendSess.boardCall("velocity.change", [boardId, iteration, velocity]);
  }

  addObjective(boardId: string, objective: Objective, isStretch: boolean) {
    this.backendSess.boardPublish(boardId, "objective.add", [
      { objective_id: objective.id, ...toServerObjective(objective, isStretch) },
    ]);
  }

  deleteObjective(boardId: string, id: string) {
    this.backendSess.boardPublish(boardId, "objective.remove", [id]);
  }

  updateObjective(boardId: string, objective: Objective) {
    this.backendSess.boardPublish(boardId, "objective.update", [
      objective.id,
      toServerObjective(objective),
    ]);
  }

  moveObjective(boardId: string, objectiveId: string, stretch: boolean, rank: number) {
    this.backendSess.boardPublish(boardId, "objective.move", [objectiveId, stretch, rank]);
  }

  async mirror(
    cardId: string,
    toBoardId: string,
    props?: Partial<Card>,
    newCardPos?: Pick<AlterCard, "x" | "y">,
  ) {
    // Convert x/y to a_center using toServerSticky which handles coordinate transformation
    const aCenter = newCardPos ? toServerSticky(newCardPos).a_center : undefined;

    const res = await this.backendSess.mirror(cardId, toBoardId, toServerSticky(props), aCenter);
    return res.created ? res.mirrored_id : undefined;
  }

  move(cardId: string, teamId: string) {
    this.backendSess.move(cardId, teamId);
  }

  toRisk(cardId: string, boardId: string, text: string, typeId: string, teamId: string) {
    this.backendSess.toRisk(cardId, boardId, text, typeId, teamId);
  }

  addLink(cardId: string, toId: string): Promise<ServerLink> {
    return this.backendSess.addLink(cardId, toId);
  }

  async addObjectiveLink(cardId: string, boardId: string, objectiveId: string): Promise<void> {
    await this.backendSess.addObjectiveLink(cardId, boardId, objectiveId);
  }

  deleteLink(linkId: string): Promise<void> {
    return this.backendSess.deleteLink(linkId);
  }

  async deleteObjectiveLink(cardId: string, boardId: string, objectiveId: string): Promise<void> {
    await this.backendSess.deleteObjectiveLink(cardId, boardId, objectiveId);
  }

  addBoard(type: string, name: string): Promise<string> {
    return this.backendSess.addBoard(type, name);
  }

  updateBoard(id: string, name: string) {
    return this.backendSess.updateBoard(id, name);
  }

  deleteBoard(id: string) {
    return this.backendSess.deleteBoard(id);
  }

  duplicateFlex(boardId: string, sessionId: string) {
    return this.backendSess.duplicateFlex(boardId, sessionId);
  }

  addCategory(name: string): Promise<string> {
    return this.backendSess.addCategory(name);
  }

  updateCategory(id: string, update: { name?: string; position?: number }) {
    return this.backendSess.updateCategory(id, update);
  }

  deleteCategory(id: string) {
    return this.backendSess.deleteCategory(id);
  }

  addBoardToCategory(boardId: string, categoryId: string) {
    return this.backendSess.addBoardToCategory(boardId, categoryId);
  }

  removeBoardFromCategory(boardId: string, categoryId: string) {
    return this.backendSess.removeBoardFromCategory(boardId, categoryId);
  }

  pointerPos(boardId: string, pos: RelativeCoordinate) {
    this.backendSess.boardPublish(boardId, "pointer.position", [pos]);
  }

  addUserOnBoard(boardId: string, userId: string) {
    this.backendSess.boardPublish(boardId, "subscribers.join", [userId]);
  }

  // not used currently
  // users are removed when the last addUserOnBoard call was too long ago
  removeUserFromBoard(boardId: string, userId: string) {
    this.backendSess.boardPublish(boardId, "subscribers.leave", [userId]);
  }

  async addReaction(cardId: string, reaction: Reaction) {
    await this.backendSess.addReaction(cardId, reaction);
  }

  async removeReaction(cardId: string, reaction: Reaction) {
    await this.backendSess.removeReaction(cardId, reaction);
  }

  addShape(boardId: string, shape: Shape) {
    return this.backendSess.addShape(boardId, shape);
  }

  editShape(boardId: string, shape: Shape) {
    return this.backendSess.editShape(boardId, shape);
  }

  removeShape(boardId: string, shapeId: string) {
    return this.backendSess.removeShape(boardId, shapeId);
  }

  async createTimer(data: TimerData, target: { boardId?: string; artId?: string }) {
    return await this.backendSess.createTimerEvent(data, target);
  }

  async updateTimer(id: string, data: Partial<TimerData>) {
    await this.backendSess.updateTimerEvent(id, data);
  }

  deleteEvent(id: string) {
    return this.backendSess.deleteEvent(id);
  }

  reloadKanbanConfigs(teamIds: string[]): Promise<Record<string, Kanban[]>> {
    return this.backendSess.getAlmKanbanConfigs(teamIds).then(mapKanbanConfigs);
  }
}

/**
 * Converts a card to a ServerSticky
 */
function toServerSticky(data?: AlterCard, priorities?: boolean): Partial<ServerSticky> {
  const value: Partial<ServerSticky> = {};
  if (data) {
    if (data.x !== undefined && data.y !== undefined) {
      value.a_center = [data.x, 1 - data.y];
    }
    if (data.zIndex !== undefined) {
      value.stack_pos = data.zIndex;
    }
    if (data.text !== undefined) {
      value.text = data.text;
    }
    if (data.status !== undefined) {
      value.status = data.status.name;
      value.status_class = data.status.statusClass;
    }
    if (data.transition !== undefined) {
      value.transition = data.transition;
    }
    if (data.type !== undefined && !isZombieType(data.type)) {
      value.type_id = data.type.id;
    }
    if (data.priority !== undefined) {
      if (priorities) {
        value.step_value = data.priority;
      } else {
        value.WSJF_value = data.priority;
      }
    }
    if (data.points !== undefined) {
      value.story_points = data.points;
    }
    if (data.teamId !== undefined) {
      value.team_id = data.teamId === null ? null : +data.teamId;
    }
    if (data.precondTeam !== undefined) {
      value.precond_team = data.precondTeam.name;
      value.precond_team_id = +data.precondTeam.id;
    }
    if (data.dependTeam !== undefined) {
      value.depend_team = data.dependTeam.name;
      value.depend_team_id = +data.dependTeam.id;
    }
    if (data.iterationId !== undefined) {
      value.iteration_number = data.iterationId;
    }
    if (data.flagType !== undefined) {
      value.flag_type = stickerToString(data.flagType);
    }
    if (data.risk !== undefined) {
      value.risk = data.risk;
    }
    if (data.almSourceId !== undefined) {
      value.alm_source = data.almSourceId;
    }
    if (data.almId !== undefined) {
      value.alm_issue_id = data.almId;
    }
    if (data.artId !== undefined) {
      value.art_id = data.artId;
    }
    if (data.assignee !== undefined) {
      value.assignee = { pip_id: data.assignee?.id || null, alm_id: null };
    }
  }
  return value;
}

function toServerObjective(
  data: Partial<Objective>,
  isStretch?: boolean,
): Partial<ServerObjective> {
  const value: Partial<ServerObjective> = {};
  if (data.id !== undefined) {
    value.objective_id = data.id;
  }
  if (data.text !== undefined) {
    value.text = data.text;
  }
  if (data.bv !== undefined) {
    value.bv = data.bv;
  }
  if (data.av !== undefined) {
    value.av = data.av;
  }
  if (data.description !== undefined) {
    value.description = data.description;
  }
  if (data.rag !== undefined) {
    value.rag = data.rag;
  }
  if (isStretch !== undefined) {
    value.is_stretch = isStretch;
  }
  return value;
}

export const writer = new BackendWriter();
